/**
 * LocationPicker Component
 *
 * Address search with Google Places and custom map support
 *
 * PROPS:
 * @prop {object} coordinates - Coordinates object { latitude, longitude }
 * @prop {string} location - Location address string
 * @prop {function} onLocationChange - Callback when location changes (coords, address)
 * @prop {boolean} required - Whether location is required
 * @prop {boolean} disabled - Whether picker is disabled
 */

import React, {useState, useEffect, useRef} from 'react';
import {useLocale} from '../../Localization/LocaleContext';
import {
  searchAddress,
  searchPlaces,
  getPlaceDetails,
  searchTradlyPlaces,
  getTradlyPlaceDetails,
  getCurrentLocation,
  reverseGeocode,
} from '../services';

// Generate UUID for session token
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const LocationPicker = ({
  coordinates = null,
  location = '',
  onLocationChange,
  required = false,
  disabled = false,
  className,
  hideLabel = false,
}) => {
  const {translate: t} = useLocale();
  const [searchText, setSearchText] = useState(location || '');
  const [addresses, setAddresses] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [sessionToken] = useState(generateUUID());
  const [error, setError] = useState('');
  const [mapType, setMapType] = useState(null); // Will be loaded from configs

  const dropdownRef = useRef(null);

  /**
   * Get map type from configs
   * map_type values: 'google_map' (Google Places) or null/other (Custom/Mapbox)
   */
  useEffect(() => {
    // Try to get from global config constant
    if (window.CONFIG_CONSTANT?.ADDRESS_CONFIGS?.map_type) {
      const configMapType = window.CONFIG_CONSTANT.ADDRESS_CONFIGS.map_type;
      setMapType(configMapType);
    } else {
      // Not configured: Tradly Maps, same default as the storefront
      setMapType('default_tp');
    }
  }, []);

  useEffect(() => {
    const rawLocation = String(location || '');
    setSearchText(rawLocation);

    // Older records can contain only "latitude, longitude". Resolve those
    // coordinates for display so the form shows a useful place name while
    // keeping the stored value unchanged until the user selects a result.
    const match = rawLocation.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!match) return undefined;
    let mounted = true;
    reverseGeocode(Number(match[1]), Number(match[2]))
      .then(response => {
        const formatted = response?.data?.formatted_address || response?.formatted_address;
        if (mounted && formatted) setSearchText(formatted);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [location]);

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Handle search address
   *
   * API Logic:
   * - If map_type === 'google_map' → Use places_search API (Google Places Autocomplete)
   * - Otherwise (null/other) → Use search API (Mapbox/Custom providers)
   */
  const handleSearchAddress = async () => {
    if (disabled || !searchText.trim()) {
      setError(t('listing_form.location_enter_address', 'Please enter an address to search'));
      return;
    }

    setError('');
    setIsLoading(true);
    setShowDropdown(true);

    try {
      let response;

      // Check map type to determine which API to use
      if (mapType === 'default_tp') {
        // Tradly Maps: Google via /v1/maps (platform key, metered per tenant)
        response = await searchTradlyPlaces(searchText.trim(), sessionToken);
      } else if (mapType === 'google_map') {
        // ✅ GOOGLE MAPS: Use Google Places Autocomplete
        // Endpoint: /v1/addresses/places_search?key=xxx&session_token=xxx
        console.log('Using Google Places Search API');
        response = await searchPlaces(searchText.trim(), sessionToken);
      } else {
        // ✅ CUSTOM/MAPBOX: Use Custom Address Search
        // Endpoint: /v1/addresses/search?key=xxx
        console.log('Using Custom Address Search API (Mapbox/Other)');
        response = await searchAddress(searchText.trim());
      }

      if (response && response.data && response.data.addresses) {
        setAddresses(response.data.addresses);
        if (response.data.addresses.length === 0) {
          setError(t('listing_form.location_no_addresses_search', 'No addresses found. Please try a different search.'));
        }
      } else {
        setAddresses([]);
        setError(t('listing_form.location_search_failed', 'Failed to search addresses. Please try again.'));
      }
    } catch (err) {
      console.error('Search error:', err);
      setAddresses([]);
      setError(t('listing_form.location_search_failed', 'Failed to search addresses. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle address selection
   *
   * Logic:
   * - If map_type === 'google_map' AND address has place_id:
   *   → Fetch full details from place_detail API
   *   → Then select location
   * - Otherwise (Custom/Mapbox):
   *   → Use address data directly (already has full details)
   *   → Select location immediately
   */
  const handleAddressSelect = async address => {
    if (disabled) return;

    // Check if this is a Google Places result that needs details fetched
    if ((mapType === 'google_map' || mapType === 'default_tp') && address.place_id) {
      // ✅ GOOGLE MAPS: Need to fetch place details
      // Endpoint: /v1/addresses/place_detail?place_id=xxx&session_token=xxx
      console.log('Fetching Google Place details for:', address.place_id);
      setIsLoadingDetails(true);
      setSelectedPlaceId(address.place_id);

      try {
        const response = mapType === 'default_tp'
          ? await getTradlyPlaceDetails(address.place_id, sessionToken)
          : await getPlaceDetails(address.place_id, sessionToken);

        if (response && response.address && response.address.location) {
          const loc = response.address.location;

          // Update search text with formatted address
          const formattedAddress = loc.formatted_address || address.description;
          setSearchText(formattedAddress);

          // Create coordinates object
          const coords = {
            latitude: loc.latitude,
            longitude: loc.longitude,
            formatted_address: formattedAddress,
          };

          // Notify parent with coords and formatted address string
          onLocationChange?.(coords, formattedAddress);

          setShowDropdown(false);
          setError('');
        } else {
          setError(t('listing_form.location_place_details_failed', 'Failed to get place details. Please try again.'));
        }
      } catch (err) {
        console.error('Place details error:', err);
        setError(t('listing_form.location_place_details_failed', 'Failed to get place details. Please try again.'));
      } finally {
        setIsLoadingDetails(false);
        setSelectedPlaceId(null);
      }
    } else {
      // ✅ CUSTOM/MAPBOX: Use data directly (already has lat/lng)
      console.log('Using Custom Map address data directly');
      const formattedAddress = address.formatted_address || address.description;
      setSearchText(formattedAddress);

      const coords = {
        latitude: address.latitude,
        longitude: address.longitude,
        formatted_address: formattedAddress,
      };

      // Notify parent with coords and formatted address string
      onLocationChange?.(coords, formattedAddress);
      setShowDropdown(false);
      setError('');
    }
  };

  /**
   * Handle current location
   */
  const handleGetCurrentLocation = async () => {
    if (disabled) return;

    setError('');
    setIsLoading(true);

    try {
      // Get current coordinates
      const coords = await getCurrentLocation();

      // Reverse geocode to get address
      const response = await reverseGeocode(coords.latitude, coords.longitude);

      let addressText = `${coords.latitude.toFixed(
        6,
      )}, ${coords.longitude.toFixed(6)}`;

      if (response && response.data && response.data.formatted_address) {
        addressText = response.data.formatted_address;
      }

      setSearchText(addressText);

      const finalCoords = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        formatted_address: addressText,
      };

      // Pass formatted address as string, not the whole response object
      onLocationChange?.(finalCoords, addressText);
    } catch (err) {
      console.error('Geolocation error:', err);
      if (err.code === 1) {
        setError(t('listing_form.location_access_denied', 'Location access denied. Please enable location permissions.'));
      } else if (err.code === 2) {
        setError(t('listing_form.location_unavailable', 'Unable to determine location. Please try again.'));
      } else if (err.code === 3) {
        setError(t('listing_form.location_timeout', 'Location request timed out. Please try again.'));
      } else {
        setError(t('listing_form.location_current_failed', 'Failed to get current location. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle clear location
   */
  const handleClearLocation = () => {
    if (disabled) return;
    setSearchText('');
    setAddresses([]);
    setShowDropdown(false);
    setError('');
    onLocationChange?.(null, '');
  };

  /**
   * Handle input change
   */
  const handleInputChange = e => {
    setSearchText(e.target.value);
    setError('');
    // Don't auto-search, user must press Enter or click Search button
  };

  /**
   * Handle key down (Enter to search)
   */
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !disabled && searchText.trim()) {
      e.preventDefault();
      handleSearchAddress();
    }
  };

  return (
    <div
      className={[
        'bg-white rounded-lg border border-gray-200 p-5',
        className,
      ].join(' ')}
      style={{backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)'}}>
      {!hideLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('listing_form.location', 'Location')} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Search Input */}
      <div className="relative mb-3" ref={dropdownRef}>
        <input
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={t('listing_form.location_search_placeholder', 'Search for a location...')}
          disabled={disabled}
          className={`
            w-full pl-3 pr-20 py-2 rounded-lg border focus:outline-none outline-none focus:ring-1 focus:ring-blue-500 focus:ring-opacity-50 text-sm
            ${
              disabled
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : 'bg-white focus:border-blue-500 border-gray-300'
            }
          `}
          style={{
            backgroundColor: 'var(--color-input)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text)',
          }}
        />

        {/* Right side buttons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {(searchText.length > 0 || isLoading || isLoadingDetails) &&
            !disabled && (
              <button
                type="button"
                onClick={handleClearLocation}
                disabled={isLoading || isLoadingDetails}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title={t('common.clear', 'Clear')}>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}

          <button
            type="button"
            onClick={handleSearchAddress}
            disabled={disabled || !searchText.trim() || isLoading}
            className={`
              p-1 rounded transition-colors
              ${
                !disabled && searchText.trim() && !isLoading
                  ? 'text-primary hover:bg-primary/10'
                  : 'text-gray-400 cursor-not-allowed'
              }
              ${isLoading ? 'animate-spin' : ''}
            `}
            title={t('common.search_2', 'Search')}>
            {isLoading ? (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Address Dropdown */}
        {showDropdown && (
          <div
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            style={{backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)'}}>
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mb-2"></div>
                <p className="text-sm">{t('listing_form.location_searching', 'Searching addresses...')}</p>
              </div>
            ) : addresses.length > 0 ? (
              addresses.map((address, index) => {
                const isLoadingThis =
                  isLoadingDetails && selectedPlaceId === address.place_id;
                const displayName = String(
                  address.description || address.formatted_address || '',
                );
                const subText =
                  address.structured_formatting?.secondary_text ||
                  (typeof address.place_name === 'string'
                    ? address.place_name
                    : null) ||
                  address.type;

                return (
                  <div
                    key={address.place_id || address.id || index}
                    onClick={() =>
                      !isLoadingThis && handleAddressSelect(address)
                    }
                    className={`
                      p-3 border-b border-gray-100 last:border-0 cursor-pointer
                      hover:opacity-80 transition-colors
                      ${isLoadingThis ? 'cursor-wait' : ''}
                    `}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{color: 'var(--color-text)'}}>
                          {address.structured_formatting?.main_text ||
                            (displayName && displayName.split(',')[0]) ||
                            'Address'}
                        </p>
                        {subText && typeof subText === 'string' && (
                          <p className="text-xs truncate" style={{color: 'var(--color-text-secondary)'}}>
                            {subText}
                          </p>
                        )}
                        {displayName && (
                          <p className="text-xs truncate" style={{color: 'var(--color-text-secondary)', opacity: 0.8}}>
                            {displayName}
                          </p>
                        )}
                      </div>
                      {isLoadingThis && (
                        <div className="flex-shrink-0">
                          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-gray-500">
                <svg
                  className="w-12 h-12 mx-auto mb-2 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm">{t('listing_form.location_no_addresses', 'No addresses found')}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('listing_form.location_try_different_search', 'Try a different search term')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="absolute z-10 w-full mt-1 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-red-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => setError('')}
                className="ml-auto text-red-500 hover:text-red-700">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={handleGetCurrentLocation}
          disabled={disabled || isLoading}
          className={`
            flex-1 px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm
            ${
              disabled || isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }
          `}>
          {isLoading ? (
            <>
              <div className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
              <span>{t('listing_form.location_getting', 'Getting location...')}</span>
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>{t('listing_form.location_use_current', 'Use Current Location')}</span>
            </>
          )}
        </button>
      </div>

      {/* Selected Location Display */}
      {coordinates && location && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">📍</span>
              <h4 className="font-semibold text-blue-800">{t('listing_form.location_selected', 'Selected Location')}</h4>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={handleClearLocation}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                {t('common.change', 'Change')}
              </button>
            )}
          </div>

          <p className="text-gray-900 mb-3">{location}</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">{t('listing_form.latitude', 'Latitude:')}</p>
              <p className="text-sm font-mono font-medium text-gray-900">
                {coordinates.latitude?.toFixed(6) || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">{t('listing_form.longitude', 'Longitude:')}</p>
              <p className="text-sm font-mono font-medium text-gray-900">
                {coordinates.longitude?.toFixed(6) || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Helper Text */}
      {!coordinates && !error && (
        <div className="mt-2 flex items-start gap-2">
          <svg
            className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-gray-600">
            {t('listing_form.location_helper', 'Search for an address or use your current location')}
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
